import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { HelperService } from './helper.service';
import { MessageService } from 'primeng/api';
declare var fnGetDeviceId: Function;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  isLoggedIn = false;
  redirectUrl: string | null = null;
  deviceInfo: any;

  constructor(private apiService: ApiService,
    private helperService: HelperService,
    public messageService: MessageService,
    private router: Router
  ) {
    
  }

  getAuthorizationToken() {
    return this.helperService.getLocalStore('aToken');
  }

  login(postData: any = []) {
    return new Promise((resolve, reject) => {
      this.apiService.post('login', postData).then((res: any) => {
        this.handleAuthResponse(res);
        resolve(res);
      });
    });
  }

  // SSO handoff: another app redirects to /sso?q=<encrypted>; q is opaque to us.
  // resolveOnError so a bad/expired q comes back instead of hanging.
  loginSso(q: string) {
    return this.apiService.get('login-sso', {}, { q }, true).then((res: any) => {
      this.handleAuthResponse(res);
      return res;
    });
  }

  // Persist whatever a login-shaped response carries: an MFA challenge, or a full session.
  private handleAuthResponse(res: any) {
    if (res?.message_code === 'mfa_required' && res?.data?.mfa_token) {
      // MFA challenge: hold the short-lived token for the OTP step in sessionStorage
      // (NOT localStorage — it must not outlive the tab or count as a logged-in session).
      sessionStorage.setItem('mfaToken', res.data.mfa_token);
      sessionStorage.setItem('mfaMobileHint', res.data.mobile_hint || '');
      this.isLoggedIn = false;
    } else if (!!res?.data?.token) {
      this.setSession(res.data);
    }
  }

  // Navigate for a login-shaped response. False = nothing actionable, caller handles the error.
  completeLoginNavigation(res: any): boolean {
    if (res?.message_code === 'mfa_required') { this.router.navigate(['/verify-otp']); return true; }
    if (!!res?.success && !!res?.data?.token) { this.postLoginRedirect(res.data.user); return true; }
    return false;
  }

  // Persist a completed login. Shared by login() (no MFA) and verifyOtp() so the
  // stored session is identical whichever path issued the token.
  private setSession(data: any) {
    this.helperService.setLocalStore('aToken', data.token);
    this.helperService.setLocalStore('aUser', data.user);
    this.helperService.setLocalStore('aActiveRole', data.last_role);
    this.helperService.setLocalStore('aRoles', data.roles);
    this.isLoggedIn = true;
  }

  verifyOtp(postData: any = {}) {
    return new Promise((resolve, reject) => {
      this.apiService.post('verify-otp', postData).then((res: any) => {
        if (!!res?.data?.token) {
          this.setSession(res.data);
          sessionStorage.removeItem('mfaToken');
          sessionStorage.removeItem('mfaMobileHint');
        }
        resolve(res);
      });
    });
  }

  resendOtp(mfa_token: string) {
    return this.apiService.post('resend-otp', { mfa_token });
  }

  // Where to land after a completed login (first login must set a password first).
  postLoginRedirect(user: any) {
    if (user?.first_login && user.first_login === 'Yes') {
      this.router.navigate(['/set-password']);
    } else {
      this.router.navigate(['/']);
    }
  }

  forgot(postData: any = []) {
    return this.apiService.post('forgot-password', postData);
  }

  changePassword(postData: any = []) {
    return this.apiService.post('change-password', postData);
  }

  checkLogin() {
    if (!!this.helperService.getLocalStore('aToken') && !!this.helperService.getLocalStore('aUser')) {
      this.isLoggedIn = true;
    }
    return this.isLoggedIn;
  }

  logout() {
    return new Promise((resolve, reject) => {
      if(this.checkLogin()) {
        this.apiService.get('logoutAll').then((res: any) => {
          if(!!res.success) {
            this.helperService.clearStorage();
            this.isLoggedIn = false;
            this.router.navigate(['/login']);
          }
          resolve(res);
        });
      } else {
        this.helperService.clearStorage();
        this.isLoggedIn = false;
        this.router.navigate(['/login']);
      }
    });
  }

  logoutAll() {
    return new Promise((resolve, reject) => {
      this.apiService.get('logoutAll').then((res: any) => {
        if (!!res.success) {
          this.helperService.clearStorage();
          this.isLoggedIn = false;
          this.router.navigate(['/login']);
        }
        resolve(res);
      });
    });
  }

  loginUserDesignationData() {
    let loginDesignationData;
    let aActiveRoleId = this.helperService.getLocalStore('aActiveRole');
    let allRolesData = this.helperService.getLocalStore('aRoles');
    if (!!aActiveRoleId && aActiveRoleId > 0 && !!allRolesData[aActiveRoleId]) {
      loginDesignationData = allRolesData[aActiveRoleId];
    }
    return loginDesignationData;
  }

  loginUserInfo() {
    return this.helperService.getLocalStore('aUser');
  }

  // fetch self permission data
  getPermissions() {
    return new Promise((resolve, reject) => {
      let deviceInfoLocal: string = this.helperService.getLocalStore('deviceInfo')
      
      if(!!deviceInfoLocal) {
        this.deviceInfo = deviceInfoLocal?.split(',');
      }

      if(!!this.deviceInfo && !!this.deviceInfo[3]) {
      } else {
        deviceInfoLocal = fnGetDeviceId();
        this.helperService.setLocalStore('deviceInfo', deviceInfoLocal);
        this.deviceInfo = deviceInfoLocal?.split(',');
      }

      let additionalParam = {
        'x-device-name': (!!this.deviceInfo[0]) ? this.deviceInfo[0] : '',
        'x-device-id': (!!this.deviceInfo[1]) ? this.deviceInfo[1] : '',
        'x-device-type': "Android",        
        'x-device-fcm-token': (!!this.deviceInfo[3]) ? this.deviceInfo[3] : '',
      }

      this.apiService.get('get_permissions', additionalParam).then((res: any) => {
        if (!!res.data) {
          // // store in localstorage
          this.helperService.setLocalStore('aPermission', res.data);
        }
        resolve(res);
      });

    })
  }

  // fetch onbehalf data
  getOnbehalfList() {
    return this.apiService.get('get_behalf');
  }

  // 
  loginAsOnbehalf(designation_id: any, userObj: any) {
    let additionalHeader = {
      'role-sub-id': designation_id.toString(),
      'role-sub-user-id': userObj?.u_id.toString()
    };
    
    // get permission of onbehalf users
    this.getOnBehalfPermissions(additionalHeader).then((jresponse: any) => {
      if(jresponse?.success) {
        this.messageService.add({severity:'success', summary:'', detail:`You are now logged in as ${userObj?.f_name_eng} ${userObj?.m_name_eng} ${userObj?.l_name_eng}.`});
        this.helperService.setLocalStore('isOnBehalf', true)
        let onBehalfData = {
          userInfo: userObj,
          additionalHeader: additionalHeader,
          onBehalfData: jresponse?.data?.sub
        }
        this.helperService.setLocalStore('onBehalfUserData', onBehalfData)
        
        let varOnbehalfStackData = this.helperService.getLocalStore('onbehalfStackData')
        if(!!varOnbehalfStackData) {
          varOnbehalfStackData.push(onBehalfData);
        } else {
          varOnbehalfStackData = [onBehalfData];
        }
        this.helperService.setLocalStore('onbehalfStackData', varOnbehalfStackData)
        

        // window.location.href = '/';
        this.router.navigate(['/']);
        this.helperService.permissionActiveEmitter.next(true);
      }
    })
  }

  // fetch onBehalf permission data
  getOnBehalfPermissions(additionalHeader: any = {}) {
    return new Promise((resolve, reject) => {
      this.apiService.get('get_permissions', additionalHeader).then((res: any) => {
        if (!!res.data) {
          // // store in localstorage
          this.helperService.setLocalStore('aPermission', res.data);
        }
        resolve(res);
      });
    });
  }

  submitNda(postData: any = {}) {
    return this.apiService.post('nda', postData);
  }
}
